/**
 * PaywallModal - Paywall dialog with dynamic pricing from API.
 *
 * Shown when a paywalled user tries to create a second carousel
 * or returns to the dashboard after using their trial.
 *
 * Fetches active plans from the API and displays them dynamically.
 * Dismissable so the user can still browse the dashboard and access
 * their existing carousel.
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Lock, MessageCircle, X, Check, LayoutGrid, Sparkles, Download, Loader2 } from 'lucide-react';
import { trackEvent } from '@/services/analyticsService';
import { useUserStatus } from '@/contexts/UserStatusContext';
import { getPlans } from '@/services/plansService';
import { buildCheckoutUrlWithUtms } from '@/hooks/useUtmCapture';
import type { Plan } from '@/types';
import { blockInSupportMode } from '@/lib/impersonation';

interface PaywallModalProps {
  open: boolean;
  onClose?: () => void;
}

const formatPrice = (value: number) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);

function getBestPrice(plan: Plan): { displayPrice: number; totalPrice: number; label: string; billingNote: string | null } {
  if (plan.priceYearly > 0) {
    const monthly = plan.priceYearly / 12;
    return { displayPrice: monthly, totalPrice: plan.priceYearly, label: '/mês', billingNote: `Cobrado anualmente (${formatPrice(plan.priceYearly)}/ano)` };
  }
  if (plan.priceQuarterly > 0) {
    const monthly = plan.priceQuarterly / 3;
    return { displayPrice: monthly, totalPrice: plan.priceQuarterly, label: '/mês', billingNote: `Cobrado trimestralmente (${formatPrice(plan.priceQuarterly)}/trim.)` };
  }
  if (plan.priceMonthly > 0) return { displayPrice: plan.priceMonthly, totalPrice: plan.priceMonthly, label: '/mês', billingNote: null };
  return { displayPrice: 0, totalPrice: 0, label: '', billingNote: null };
}

function formatLimit(value: number | undefined): string | null {
  if (value === undefined || value === 0) return null;
  return value === -1 ? 'Ilimitados' : `${value}/mês`;
}

const PaywallModal: React.FC<PaywallModalProps> = ({ open, onClose }) => {
  const { status } = useUserStatus();
  const navigate = useNavigate();
  const [plans, setPlans] = useState<Plan[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      trackEvent('paywall_shown');
      setLoading(true);
      getPlans()
        .then((data) => setPlans(data.plans))
        .catch(() => {})
        .finally(() => setLoading(false));
    }
  }, [open]);

  if (!open) return null;

  // Determine if user has an active paid plan (not trial/free)
  const hasPaidPlan = status?.planName && status.planName !== 'none' && status.planName !== 'Trial' && !status?.isTrialing;

  // Build contextual header and usage text
  let headerText = 'Seu trial acabou';
  let usageText = 'Você usou seu carrossel gratuito.';

  if (status?.limits && status?.usage) {
    const planLabel = status.planName || 'Trial';
    if (hasPaidPlan && status.limits.carousel_limit !== -1 && status.usage.carousels >= status.limits.carousel_limit) {
      headerText = 'Limite mensal atingido';
      usageText = `Você usou ${status.usage.carousels}/${status.limits.carousel_limit} carrosséis do plano ${planLabel} este mês.`;
    } else if (status?.isTrialing) {
      headerText = 'Limite do trial atingido';
      usageText = `Você atingiu o limite diário do trial. Assine para criar carrosséis ilimitados.`;
    } else {
      usageText = `Você usou ${status.usage.carousels}/${status.limits.carousel_limit === -1 ? 'ilimitado' : status.limits.carousel_limit} carrosséis do plano ${planLabel}.`;
    }
  }

  const handleSelectPlan = (plan: Plan) => {
    if (blockInSupportMode()) return;
    trackEvent('checkout_clicked', { source: 'paywall_modal', planId: plan.planId });
    if (plan.checkoutUrl) {
      window.open(buildCheckoutUrlWithUtms(plan.checkoutUrl), '_blank');
    } else {
      if (onClose) onClose();
      navigate(`/meus-planos?planId=${encodeURIComponent(plan.planId)}`);
    }
  };

  const handleContactSupport = () => {
    trackEvent('support_clicked', { source: 'paywall_modal' });
    window.open('https://wa.me/5511920052080', '_blank');
  };

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[60] p-4">
      <div className="bg-card rounded-2xl w-full max-w-4xl shadow-2xl border border-border overflow-hidden">
        {/* Header */}
        <div className="px-6 pt-8 pb-4 text-center relative">
          {onClose && (
            <Button
              variant="ghost"
              size="icon"
              onClick={onClose}
              className="absolute top-3 right-3 h-8 w-8"
            >
              <X className="h-4 w-4" />
            </Button>
          )}
          <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
            <Lock className="h-7 w-7 text-primary" />
          </div>
          <h2 className="text-xl font-bold">{headerText}</h2>
          <p className="text-sm text-muted-foreground mt-2">
            {usageText}{!hasPaidPlan ? ' Para continuar criando, escolha um plano.' : ''}
          </p>
        </div>

        {/* Plan Cards */}
        <div className="px-6 pb-4">
          {loading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <div className={`grid gap-4 ${
              plans.length === 1 ? 'grid-cols-1 max-w-xs mx-auto' :
              plans.length === 2 ? 'grid-cols-1 md:grid-cols-2' :
              'grid-cols-1 md:grid-cols-2 lg:grid-cols-3'
            }`}>
              {plans.map((plan) => {
                const { displayPrice, billingNote, label } = getBestPrice(plan);
                const isHighlighted = !!plan.highlightColor;
                const carousels = formatLimit(plan.carouselLimit);
                const generations = formatLimit(plan.generationLimit);
                const exports = formatLimit(plan.exportLimit);

                return (
                  <div
                    key={plan.planId}
                    className={`rounded-xl p-5 bg-card relative flex flex-col ${
                      isHighlighted
                        ? 'border-2 border-primary'
                        : 'border border-border'
                    }`}
                  >
                    {/* Plan name */}
                    <h3 className="text-lg font-bold">{plan.name}</h3>

                    {/* Price */}
                    <div className="mt-2">
                      <div>
                        <span className="text-2xl font-bold">{formatPrice(displayPrice)}</span>
                        <span className="text-sm text-muted-foreground">{label}</span>
                      </div>
                      {billingNote && (
                        <p className="text-xs text-muted-foreground mt-1">{billingNote}</p>
                      )}
                    </div>

                    {/* Limits */}
                    <ul className="mt-4 space-y-2">
                      {carousels && (
                        <li className="flex items-center gap-2 text-sm">
                          <LayoutGrid className="h-4 w-4 text-primary flex-shrink-0" />
                          {plan.carouselLimit === -1 ? 'Carrosséis ilimitados' : `${plan.carouselLimit} carrosséis/mês`}
                        </li>
                      )}
                      {generations && (
                        <li className="flex items-center gap-2 text-sm">
                          <Sparkles className="h-4 w-4 text-primary flex-shrink-0" />
                          {plan.generationLimit === -1 ? 'Gerações IA ilimitadas' : `${plan.generationLimit} gerações IA/mês`}
                        </li>
                      )}
                      {exports && (
                        <li className="flex items-center gap-2 text-sm">
                          <Download className="h-4 w-4 text-primary flex-shrink-0" />
                          {plan.exportLimit === -1 ? 'Exportações ilimitadas' : `${plan.exportLimit} exportações/mês`}
                        </li>
                      )}
                    </ul>

                    {/* Features */}
                    {plan.features.length > 0 && (
                      <ul className="mt-3 pt-3 border-t border-border space-y-2 flex-1">
                        {plan.features.map((feature, idx) => (
                          <li key={idx} className="flex items-center gap-2 text-sm">
                            <Check className="h-4 w-4 text-primary flex-shrink-0" />
                            {feature}
                          </li>
                        ))}
                      </ul>
                    )}

                    {/* CTA */}
                    <Button
                      onClick={() => handleSelectPlan(plan)}
                      variant={isHighlighted ? 'default' : 'outline'}
                      className="w-full mt-5"
                      size="lg"
                    >
                      Selecionar plano
                    </Button>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Support + Footer */}
        <div className="px-6 pb-6 pt-2 space-y-3">
          <Button
            variant="outline"
            onClick={handleContactSupport}
            className="w-full"
          >
            <MessageCircle className="h-4 w-4 mr-2" />
            Falar com suporte
          </Button>
          <p className="text-xs text-muted-foreground text-center">
            Dúvidas? Entre em contato pelo WhatsApp ou email.
          </p>
        </div>
      </div>
    </div>
  );
};

export default PaywallModal;
